import React, { useEffect, useMemo } from 'react';
import Modal from '../../BaseComponents/Modal';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../BaseComponents/Loader';
import { RootState } from '../../../store/store';
import Select from '../../BaseComponents/Select';
import Button from '../../BaseComponents/Button';
import './MemberInfoCard.scss';
import { getNutritionistsList } from '../../../api/nutritionist';
import { setNutritionistsList } from '../../../store/nutritionistsSlice';
import { assignNutritionistToMember } from '../../../api/members';
import {
  changeAssignNutritionistModalVisibility,
  setMemberData
} from '../../../store/memberSlice';

type AssignNutritionistModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const AssignNutritionistModal: React.FC<AssignNutritionistModalProps> = ({
  isOpen,
  onClose
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<boolean>(false);
  const [selectedOption, setSelectedOption] = React.useState<any>(false);

  const { nutritionistsList } = useSelector(
    (state: RootState) => state.nutritionists
  );
  const { memberData, assignedNutritionistId } = useSelector(
    (state: RootState) => state.member
  );

  useEffect(() => {
    const fetchClinics = async () => {
      try {
        setLoading(true);
        const nutritionistsList = await getNutritionistsList(0, 1000);
        dispatch(setNutritionistsList(nutritionistsList.nutritionists));
        if (assignedNutritionistId && nutritionistsList.nutritionists) {
          const assigned = nutritionistsList.nutritionists.find(
            (doctor) => doctor.id === assignedNutritionistId
          );
          if (assigned) {
            setSelectedOption({
              key: assigned.id,
              value: `${assigned.first_name} ${assigned.last_name}`
            });
          }
        }
      } catch (error) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchClinics();
  }, [assignedNutritionistId, dispatch]);

  const nutritionistsListOptions = useMemo(() => {
    return nutritionistsList
      ? nutritionistsList.map((nutritionist) => ({
          key: nutritionist.id,
          value: `${nutritionist.first_name} ${nutritionist.last_name}`
        }))
      : [];
  }, [nutritionistsList]);

  const onAssignClick = async () => {
    try {
      setLoading(true);
      await assignNutritionistToMember({
        nutritionist_id: selectedOption.key,
        member_id: memberData?.user_id!
      });
      const selectedNutritionist = nutritionistsList?.find(
        (nutritionist) => nutritionist.id === selectedOption.key
      );

      if (selectedNutritionist && memberData) {
        dispatch(
          setMemberData({
            ...memberData,
            nutritionist: {
              first_name: selectedNutritionist.first_name,
              last_name: selectedNutritionist.last_name,
              photo_url: selectedNutritionist.photo_url,
              user_id: selectedNutritionist.id
            }
          })
        );
      }

      onClose();
    } catch (error) {
      console.log('error assigning Nutritionist', error);
    } finally {
      setLoading(false);
      dispatch(changeAssignNutritionistModalVisibility(false));
    }
  };

  if (loading) return <Loader />;
  if (error) return <div>Error fetching nutritionists list</div>;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => dispatch(changeAssignNutritionistModalVisibility(false))}
      width={'400px'}
    >
      <div className='assign-doctor-modal'>
        <div className='assign-header'>
          Please select nutritionist to assign:
        </div>
        <div className='select-doctor-container'>
          <Select
            name='nutritionist'
            placeholder='Select nutritionist'
            options={nutritionistsListOptions}
            selectedOption={selectedOption}
            onChange={(option) => setSelectedOption(option)}
            isError={false}
          />
        </div>

        <div className='submit-btn-wrapper'>
          <Button text='Assign' onClick={onAssignClick} isLoading={loading} />
          <Button
            isLoading={loading}
            text='Cancel'
            onClick={() =>
              dispatch(changeAssignNutritionistModalVisibility(false))
            }
            backgroundColor={'#ffffff'}
            color={'#2D334E'}
          />
        </div>
        {error && (
          <div className='error-message'>
            Something went wrong. Please try again later
          </div>
        )}
      </div>
    </Modal>
  );
};
export default AssignNutritionistModal;
